import { FormEvent, useEffect, useRef, useState } from "react";
import { ImagePlus, RefreshCw, RotateCcw, Save, ShieldCheck, Trash2 } from "lucide-react";
import { seedArtworks } from "../data/artworks";
import { formatArtworkPrice, formatAvailability } from "../lib/artworkApi";
import type { Artwork, StockStatus } from "../types/artwork";

interface AdminFormState {
  title: string;
  sku: string;
  pricePounds: string;
  stockStatus: StockStatus;
  published: "true" | "false";
  description: string;
}

const emptyForm: AdminFormState = {
  title: "",
  sku: "",
  pricePounds: "",
  stockStatus: "instock",
  published: "true",
  description: ""
};

export function AdminApp() {
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [formState, setFormState] = useState<AdminFormState>(emptyForm);
  const [token, setToken] = useState(() => window.localStorage.getItem("jan_admin_token") ?? "");
  const [status, setStatus] = useState("Loading catalogue");
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void loadAdminArtworks();
  }, []);

  async function loadAdminArtworks() {
    setStatus("Loading catalogue");
    try {
      const response = await fetch("/api/admin/artworks", {
        headers: adminHeaders(token)
      });
      if (!response.ok) throw new Error(`Admin API returned ${response.status}`);
      const data = (await response.json()) as { artworks?: Artwork[] };
      if (!Array.isArray(data.artworks)) throw new Error("Admin API response missing artworks");
      setArtworks(data.artworks);
      setStatus("Cloudflare catalogue loaded");
    } catch {
      setArtworks(seedArtworks);
      setStatus("Using local WordPress seed data until Cloudflare bindings are connected");
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    if (selectedArtwork) formData.set("id", selectedArtwork.id);

    setSaving(true);
    setStatus(selectedArtwork ? "Saving artwork changes" : "Uploading artwork");
    try {
      const response = await fetch("/api/admin/artworks", {
        method: selectedArtwork ? "PUT" : "POST",
        headers: adminHeaders(token),
        body: formData
      });
      if (!response.ok) throw new Error(`Save failed with ${response.status}`);
      const data = (await response.json()) as { artwork: Artwork };

      setArtworks((current) =>
        selectedArtwork
          ? current.map((artwork) => (artwork.id === data.artwork.id ? data.artwork : artwork))
          : [data.artwork, ...current]
      );
      setSelectedArtwork(selectedArtwork ? data.artwork : null);
      if (selectedArtwork) {
        setFormState(formStateFromArtwork(data.artwork));
        clearFileInput();
      } else {
        resetForm();
      }
      setStatus(selectedArtwork ? `${data.artwork.title} has been updated` : `${data.artwork.title} is now in the 3D gallery`);
    } catch {
      setStatus("Save failed. Check Cloudflare bindings or your admin token.");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveSelected() {
    if (!selectedArtwork) return;
    const confirmed = window.confirm(`Remove "${selectedArtwork.title}" from the public site?`);
    if (!confirmed) return;

    setSaving(true);
    setStatus(`Removing ${selectedArtwork.title} from the public site`);
    try {
      const response = await fetch(`/api/admin/artworks?id=${encodeURIComponent(selectedArtwork.id)}`, {
        method: "DELETE",
        headers: adminHeaders(token)
      });
      if (!response.ok) throw new Error(`Delete failed with ${response.status}`);
      const data = (await response.json()) as { artwork: Artwork };
      setArtworks((current) => current.map((artwork) => (artwork.id === data.artwork.id ? data.artwork : artwork)));
      setSelectedArtwork(data.artwork);
      setFormState(formStateFromArtwork(data.artwork));
      setStatus(`${data.artwork.title} is now hidden from the public gallery`);
    } catch {
      setStatus("Remove failed. Check Cloudflare bindings or your admin token.");
    } finally {
      setSaving(false);
    }
  }

  function selectArtwork(artwork: Artwork) {
    setSelectedArtwork(artwork);
    setFormState(formStateFromArtwork(artwork));
    clearFileInput();
    setStatus(`Editing ${artwork.title}`);
  }

  function resetForm() {
    setSelectedArtwork(null);
    setFormState(emptyForm);
    clearFileInput();
    formRef.current?.reset();
    setStatus("Ready to add a new artwork");
  }

  function clearFileInput() {
    if (fileRef.current) fileRef.current.value = "";
  }

  function handleTokenChange(value: string) {
    setToken(value);
    if (value) {
      window.localStorage.setItem("jan_admin_token", value);
    } else {
      window.localStorage.removeItem("jan_admin_token");
    }
  }

  const editing = Boolean(selectedArtwork);

  return (
    <main className="admin-shell">
      <section className="admin-header">
        <a className="brand" href="/" aria-label="Jan Husband gallery">
          <ImagePlus aria-hidden="true" />
          <span>Jan Husband</span>
        </a>
        <div className="admin-status">
          <ShieldCheck aria-hidden="true" />
          <span>{status}</span>
        </div>
      </section>

      <section className="admin-layout" aria-label="Artwork admin">
        <form ref={formRef} className="admin-uploader" onSubmit={handleSubmit}>
          <div className="section-heading">
            <p className="eyebrow">Studio admin</p>
            <h1>{editing ? "Edit Artwork" : "Add Artwork"}</h1>
          </div>

          <label className="admin-field">
            Local admin token
            <input
              value={token}
              onChange={(event) => handleTokenChange(event.target.value)}
              type="password"
              autoComplete="off"
              placeholder="Only needed for local dev token access"
            />
          </label>

          <label className="admin-field">
            Title
            <input
              name="title"
              required
              placeholder="Painting title"
              value={formState.title}
              onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
            />
          </label>

          <label className="admin-field">
            Image
            <input
              ref={fileRef}
              name="image"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              required={!editing}
            />
            {editing && <span className="admin-help">Leave blank to keep the existing image.</span>}
          </label>

          <div className="admin-two">
            <label className="admin-field">
              SKU
              <input
                name="sku"
                placeholder="JHA030"
                value={formState.sku}
                onChange={(event) => setFormState((current) => ({ ...current, sku: event.target.value }))}
              />
            </label>
            <label className="admin-field">
              Price
              <input
                name="pricePounds"
                type="number"
                min="0"
                step="1"
                placeholder="145"
                value={formState.pricePounds}
                onChange={(event) => setFormState((current) => ({ ...current, pricePounds: event.target.value }))}
              />
            </label>
          </div>

          <div className="admin-two">
            <label className="admin-field">
              Status
              <select
                name="stockStatus"
                value={formState.stockStatus}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, stockStatus: event.target.value as StockStatus }))
                }
              >
                <option value="instock">Available</option>
                <option value="outofstock">Sold</option>
                <option value="onbackorder">Reserved</option>
                <option value="unknown">On enquiry</option>
              </select>
            </label>
            <label className="admin-field">
              Visibility
              <select
                name="published"
                value={formState.published}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, published: event.target.value as "true" | "false" }))
                }
              >
                <option value="true">Published</option>
                <option value="false">Draft</option>
              </select>
            </label>
          </div>

          <label className="admin-field">
            Description
            <textarea
              name="description"
              rows={6}
              placeholder="Medium, dimensions, story, or exhibition notes"
              value={formState.description}
              onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))}
            />
          </label>

          <div className="admin-actions">
            <button className="primary-button" type="submit" disabled={saving}>
              <Save aria-hidden="true" />
              {saving ? "Saving" : editing ? "Save changes" : "Upload artwork"}
            </button>
            {editing && (
              <>
                <button className="secondary-button" type="button" onClick={resetForm} disabled={saving}>
                  <RotateCcw aria-hidden="true" />
                  New artwork
                </button>
                <button className="danger-button" type="button" onClick={handleRemoveSelected} disabled={saving}>
                  <Trash2 aria-hidden="true" />
                  Remove from public site
                </button>
              </>
            )}
          </div>
        </form>

        <section className="admin-catalogue">
          <div className="admin-catalogue-head">
            <div>
              <p className="eyebrow">{artworks.length} works</p>
              <h2>Gallery Dataset</h2>
            </div>
            <button className="icon-button" type="button" onClick={loadAdminArtworks} aria-label="Refresh catalogue">
              <RefreshCw aria-hidden="true" />
            </button>
          </div>

          <div className="admin-list">
            {artworks.map((artwork) => (
              <button
                className={`admin-row ${selectedArtwork?.id === artwork.id ? "selected" : ""}`}
                key={artwork.id}
                type="button"
                onClick={() => selectArtwork(artwork)}
                aria-label={`Edit ${artwork.title}`}
              >
                {artwork.imageUrl ? <img src={artwork.imageUrl} alt="" /> : <span>{artwork.title}</span>}
                <div>
                  <strong>{artwork.title}</strong>
                  <small>
                    {artwork.sku} · {formatArtworkPrice(artwork)} · {formatAvailability(artwork)} ·{" "}
                    {artwork.published ? "Published" : "Draft"}
                  </small>
                </div>
              </button>
            ))}
          </div>
        </section>
      </section>
    </main>
  );
}

function formStateFromArtwork(artwork: Artwork): AdminFormState {
  return {
    title: artwork.title,
    sku: artwork.sku,
    pricePounds: artwork.pricePence === null ? "" : String(Math.round(artwork.pricePence / 100)),
    stockStatus: artwork.stockStatus,
    published: artwork.published ? "true" : "false",
    description: artwork.description
  };
}

function adminHeaders(token: string): HeadersInit {
  return token ? { "x-admin-token": token } : {};
}
